import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { discussionsAPI } from '../api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  MessageSquare, 
  ThumbsUp, 
  HelpCircle, 
  Reply, 
  MoreVertical,
  Send,
  Flag
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import ArticleAttachmentPicker from '../components/ArticleAttachmentPicker';
import AttachedArticleCard from '../components/AttachedArticleCard';
import PhiBanner from '../components/PhiBanner';
import VerificationCallout from '../components/VerificationCallout';
import { litScholarAPI } from '../api';
import type { ThreadDetailResponse, DiscussionComment, ReactionType, AttachableArticle, AttachedArticle } from '../types';

const ThreadDetailPage: React.FC = () => {
  const { threadId } = useParams<{ threadId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canWrite = user?.capabilities?.community_write !== false;
  const canReact = user?.capabilities?.community_react !== false;
  const canDraft = user?.capabilities?.copilot_draft_post === true && canWrite;
  const [thread, setThread] = useState<ThreadDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [selectedArticles, setSelectedArticles] = useState<AttachableArticle[]>([]);
  const [replyArticles, setReplyArticles] = useState<AttachableArticle[]>([]);

  useEffect(() => {
    if (threadId) {
      loadThread();
    }
  }, [threadId]);

  const loadThread = async (): Promise<void> => {
    if (!threadId) return;
    try {
      const data = await discussionsAPI.getThreadDetail(threadId);
      setThread(data);
    } catch (error) {
      console.error('Error loading thread:', error);
      toast.error('Failed to load discussion');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitComment = async (): Promise<void> => {
    if (!threadId || !newComment.trim()) return;
    
    setSubmitting(true);
    try {
      const attachedIds = selectedArticles.map(a => a.pmid);
      await discussionsAPI.createComment(threadId, { 
        body: newComment.trim(),
        attached_article_ids: attachedIds.length > 0 ? attachedIds : undefined
      });
      setNewComment('');
      setSelectedArticles([]);
      await loadThread();
      toast.success('Comment posted!');
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      if (detail?.error_code === 'phi_detected') {
        toast.error(detail.message || 'PHI detected. Please remove patient identifiers.');
      } else if (detail?.error_code === 'verification_required') {
        toast.error('Work email verification required to post. Go to Settings > Professional Verification.');
      } else {
        toast.error('Failed to post comment');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitReply = async (parentCommentId: string): Promise<void> => {
    if (!threadId || !replyText.trim()) return;
    
    setSubmitting(true);
    try {
      const attachedIds = replyArticles.map(a => a.pmid);
      await discussionsAPI.createComment(threadId, { 
        body: replyText.trim(),
        parent_comment_id: parentCommentId,
        attached_article_ids: attachedIds.length > 0 ? attachedIds : undefined
      });
      setReplyText('');
      setReplyingTo(null);
      setReplyArticles([]);
      await loadThread();
      toast.success('Reply posted!');
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      if (detail?.error_code === 'phi_detected') {
        toast.error(detail.message || 'PHI detected. Please remove patient identifiers.');
      } else if (detail?.error_code === 'verification_required') {
        toast.error('Work email verification required to reply. Go to Settings > Professional Verification.');
      } else {
        toast.error('Failed to post reply');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleReaction = async (commentId: string, reactionType: ReactionType): Promise<void> => {
    try {
      await discussionsAPI.addReaction(commentId, reactionType);
      await loadThread();
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      if (detail?.error_code === 'verification_required') {
        toast.error('Verification required to react.');
      } else {
        toast.error('Failed to add reaction');
      }
    }
  };

  const [reportingCommentId, setReportingCommentId] = useState<string | null>(null);
  const [reportCategory, setReportCategory] = useState<string>('other');
  const [reportReason, setReportReason] = useState('');

  const handleReport = async (): Promise<void> => {
    if (!reportingCommentId || !reportReason.trim()) return;
    try {
      await discussionsAPI.reportComment(reportingCommentId, reportReason.trim(), reportCategory);
      toast.success('Report submitted. Thank you for helping keep our community safe.');
      setReportingCommentId(null);
      setReportCategory('other');
      setReportReason('');
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      if (detail?.error_code === 'phi_detected') {
        toast.error('Report reason contains PHI. Please remove patient identifiers.');
      } else {
        toast.error('Failed to submit report');
      }
    }
  };

  const formatTimeAgo = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getReactionIcon = (type: ReactionType) => {
    switch (type) {
      case 'helpful': return <ThumbsUp className="h-3.5 w-3.5" />;
      case 'question': return <HelpCircle className="h-3.5 w-3.5" />;
      default: return null;
    }
  };

  const getReactionLabel = (type: ReactionType): string => {
    switch (type) {
      case 'helpful': return 'Like';
      case 'question': return 'Question';
      default: return type;
    }
  };

  const getReactionCount = (comment: DiscussionComment, type: ReactionType): number => {
    if (type === 'helpful') {
      return (
        comment.reactions?.helpful?.length ||
        comment.reactions?.like?.length ||
        0
      );
    }
    return comment.reactions?.[type]?.length || 0;
  };

  const hasUserReacted = (comment: DiscussionComment, type: ReactionType): boolean => {
    const uid = user?.user_id || '';
    if (type === 'helpful') {
      return (
        comment.reactions?.helpful?.includes(uid) ||
        comment.reactions?.like?.includes(uid) ||
        false
      );
    }
    return comment.reactions?.[type]?.includes(uid) || false;
  };

  /** Cap visual indent so deep threads stay readable */
  const MAX_INDENT_DEPTH = 6;

  const getChildren = (comment: DiscussionComment): DiscussionComment[] => {
    if (Array.isArray(comment.children)) return comment.children;
    return (thread?.comments || []).filter(
      (c) => c.parent_comment_id === comment.comment_id
    );
  };

  /** Build a nested tree from the flat comments list (fallback if comment_tree is missing). */
  const buildCommentTree = (comments: DiscussionComment[]): DiscussionComment[] => {
    const byId: Record<string, DiscussionComment & { children: DiscussionComment[] }> =
      Object.fromEntries(
        comments.map((c) => [c.comment_id, { ...c, children: [] as DiscussionComment[] }])
      );
    const roots: DiscussionComment[] = [];
    for (const c of comments) {
      const node = byId[c.comment_id];
      const parent = c.parent_comment_id ? byId[c.parent_comment_id] : null;
      if (parent) parent.children.push(node);
      else roots.push(node);
    }
    return roots;
  };

  const renderComment = (comment: DiscussionComment, depthOverride?: number) => {
    const depth =
      depthOverride ??
      comment.depth ??
      (comment.parent_comment_id ? 1 : 0);
    const children = getChildren(comment);
    const isNested = depth > 0;

    // Soft-deleted (moderated) comment — show placeholder, keep children attached
    if (comment.deleted_at) {
      return (
        <div
          key={comment.comment_id}
          className={`min-w-0 ${
            isNested
              ? depth <= MAX_INDENT_DEPTH
                ? 'ml-2 sm:ml-3 border-l-2 border-gray-100 pl-2'
                : 'border-l-2 border-gray-100 pl-2'
              : ''
          }`}
          data-testid={`comment-${comment.comment_id}`}
          data-depth={depth}
        >
          <div className="py-3 px-4 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-sm text-gray-500 italic" data-testid="moderated-comment">
              This comment was removed by moderation.
            </p>
          </div>
          {children.length > 0 && (
            <div className="mt-3 space-y-3 min-w-0">
              {children.map((child) => renderComment(child, depth + 1))}
            </div>
          )}
        </div>
      );
    }

    return (
    <div 
      key={comment.comment_id} 
      className={`min-w-0 ${
        isNested
          ? depth <= MAX_INDENT_DEPTH
            ? 'ml-2 sm:ml-3 border-l-2 border-gray-100 pl-2'
            : 'border-l-2 border-gray-100 pl-2'
          : ''
      }`}
      data-testid={`comment-${comment.comment_id}`}
      data-depth={depth}
    >
      <div className="flex items-start gap-2 sm:gap-3 min-w-0">
        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-medium text-sm flex-shrink-0">
          {(comment.user_name || 'A')[0].toUpperCase()}
        </div>
        <div className="flex-1 min-w-0 overflow-hidden">
          <div className="flex items-start justify-between gap-2">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0">
              <span className="font-medium text-gray-900 text-sm">
                {comment.user_name || 'Anonymous'}
              </span>
              {comment.author_is_verified && (
                <Badge variant="secondary" className="h-5 px-1.5 text-[10px] font-medium bg-emerald-100 text-emerald-700 border-0" data-testid="verified-badge">
                  Verified
                </Badge>
              )}
              <span className="text-xs text-gray-500">
                {formatTimeAgo(comment.created_at)}
              </span>
              {comment.updated_at !== comment.created_at && (
                <span className="text-xs text-gray-400">(edited)</span>
              )}
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0 shrink-0">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setReportingCommentId(comment.comment_id)}>
                  <Flag className="h-4 w-4 mr-2" />
                  Report
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          
          <p className="text-gray-700 mt-1 text-sm whitespace-pre-wrap break-words">
            {comment.body}
          </p>

          {/* Attached Articles */}
          {comment.attached_articles && comment.attached_articles.length > 0 && (
            <div className="mt-3 space-y-2 min-w-0">
              {comment.attached_articles.map((article) => (
                <AttachedArticleCard key={article.pmid} article={article} />
              ))}
            </div>
          )}

          {/* Reactions + Reply at every depth */}
          <div className="flex flex-wrap items-center gap-1 mt-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => canReact && handleReaction(comment.comment_id, 'helpful')}
              disabled={!canReact}
              className={`h-7 px-2 text-xs ${
                hasUserReacted(comment, 'helpful') ? 'bg-blue-50 text-blue-600' : 'text-gray-500'
              } ${!canReact ? 'opacity-50 cursor-not-allowed' : ''}`}
              title={!canReact ? 'Verify your work email to react' : ''}
            >
              {getReactionIcon('helpful')}
              <span className="ml-1">{getReactionLabel('helpful')}</span>
              {getReactionCount(comment, 'helpful') > 0 && (
                <span className="ml-1">({getReactionCount(comment, 'helpful')})</span>
              )}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => canReact && handleReaction(comment.comment_id, 'question')}
              disabled={!canReact}
              className={`h-7 w-full justify-start px-2 text-xs basis-full ${
                hasUserReacted(comment, 'question') ? 'bg-blue-50 text-blue-600' : 'text-gray-500'
              } ${!canReact ? 'opacity-50 cursor-not-allowed' : ''}`}
              title={!canReact ? 'Verify your work email to react' : ''}
            >
              {getReactionIcon('question')}
              <span className="ml-1">{getReactionLabel('question')}</span>
              {getReactionCount(comment, 'question') > 0 && (
                <span className="ml-1">({getReactionCount(comment, 'question')})</span>
              )}
            </Button>
            {canWrite && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setReplyingTo(replyingTo === comment.comment_id ? null : comment.comment_id);
                  setReplyText('');
                  setReplyArticles([]);
                }}
                className="h-7 w-full justify-start px-2 text-xs text-gray-500 basis-full"
                data-testid={`reply-btn-${comment.comment_id}`}
              >
                <Reply className="h-3.5 w-3.5 mr-1" />
                Reply
                {(comment.reply_count || children.length) > 0 &&
                  ` (${comment.reply_count || children.length})`}
              </Button>
            )}
          </div>

          {/* Reply Input — works for any depth */}
          {replyingTo === comment.comment_id && (
            <div className="mt-3 space-y-2 min-w-0">
              <Textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Write a reply..."
                className="min-h-[60px] w-full text-sm"
                data-testid="reply-input"
              />
              <div className="flex flex-col gap-2">
                <ArticleAttachmentPicker
                  selectedArticles={replyArticles}
                  onSelectionChange={setReplyArticles}
                  maxArticles={3}
                />
                <Button
                  onClick={() => handleSubmitReply(comment.comment_id)}
                  disabled={!replyText.trim() || submitting}
                  size="sm"
                  className="w-full"
                  data-testid="submit-reply-btn"
                >
                  <Send className="h-4 w-4 mr-1" />
                  Reply
                </Button>
              </div>
            </div>
          )}

          {/* Nested children (unlimited depth) */}
          {children.length > 0 && (
            <div className="mt-3 space-y-3 min-w-0">
              {children.map((child) => renderComment(child, depth + 1))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading discussion...</p>
        </div>
      </div>
    );
  }

  if (!thread) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900">Discussion not found</h2>
          <Button onClick={() => navigate('/community')} className="mt-4">
            Back to Community
          </Button>
        </div>
      </div>
    );
  }

  // Prefer server comment_tree; fall back to nesting the flat list
  const rootComments =
    thread.comment_tree && thread.comment_tree.length > 0
      ? thread.comment_tree
      : buildCommentTree(thread.comments || []);

  return (
    <div className="min-h-screen bg-gray-50 py-8 overflow-x-hidden" data-testid="thread-detail-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 min-w-0">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate('/community')}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Community
        </Button>

        {/* Thread Header */}
        <Card className="mb-6 overflow-hidden">
          <CardHeader className="min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              {thread.is_pinned && (
                <Badge variant="secondary">Pinned</Badge>
              )}
              <Badge variant="outline">{thread.context_type}</Badge>
            </div>
            <CardTitle className="responsive-title text-xl break-words">{thread.title}</CardTitle>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500 mt-2">
              <span>Started by {thread.creator_name || 'Anonymous'}</span>
              <span>{formatTimeAgo(thread.created_at)}</span>
              <span className="flex items-center gap-1">
                <MessageSquare className="h-4 w-4" />
                {thread.comment_count} comments
              </span>
            </div>
          </CardHeader>
        </Card>

        {/* Comments Section */}
        <Card className="overflow-hidden">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-blue-600 shrink-0" />
              Discussion
            </CardTitle>
          </CardHeader>
          <CardContent className="min-w-0">
            {/* New Comment Form or Verification Callout */}
            <div className="mb-6 pb-6 border-b min-w-0">
              {canWrite ? (
                <>
                  <div className="mb-2">
                    <PhiBanner compact />
                  </div>
                  <Textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Share your thoughts on this discussion..."
                    className="min-h-[100px] w-full"
                    data-testid="new-comment-input"
                  />
                  {/* Selected Articles Preview */}
                  {selectedArticles.length > 0 && (
                    <div className="mt-3 space-y-2 min-w-0">
                      {selectedArticles.map(article => (
                        <AttachedArticleCard key={article.pmid} article={article as AttachedArticle} compact />
                      ))}
                    </div>
                  )}
                  <div className="flex flex-col gap-2 mt-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <ArticleAttachmentPicker
                        selectedArticles={selectedArticles}
                        onSelectionChange={setSelectedArticles}
                        maxArticles={3}
                      />
                      {canDraft && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={async () => {
                            try {
                              const pmids = selectedArticles.map(a => a.pmid).filter(Boolean);
                              if (pmids.length === 0) { toast('Attach articles first to draft with Lit Scholar'); return; }
                              const result = await litScholarAPI.draftPost({ specialty_id: thread?.specialty_id || 'general', pmids });
                              if (result.draft_post) { setNewComment(result.draft_post); toast.success('Draft inserted — edit before posting'); }
                            } catch (e: any) {
                              const d = e?.response?.data?.detail;
                              if (d?.error_code === 'copilot_disabled') toast.error('Lit Scholar unavailable');
                              else toast.error('Draft failed');
                            }
                          }}
                          data-testid="draft-litscholar-btn"
                        >
                          Draft with Lit Scholar
                        </Button>
                      )}
                    </div>
                    <Button
                      onClick={handleSubmitComment}
                      disabled={!newComment.trim() || submitting}
                      className="w-full"
                      data-testid="submit-comment-btn"
                    >
                      <Send className="h-4 w-4 mr-2" />
                      Post Comment
                    </Button>
                  </div>
                </>
              ) : (
                <VerificationCallout />
              )}
            </div>

            {/* Comments List — nested tree */}
            <div className="space-y-6 min-w-0">
              {rootComments.length > 0 ? (
                rootComments.map((comment) => renderComment(comment, comment.depth ?? 0))
              ) : (
                <div className="text-center py-8">
                  <MessageSquare className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No comments yet. Be the first to share your thoughts!</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Report Dialog */}
        {reportingCommentId && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="report-dialog">
            <Card className="w-full max-w-md mx-4">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Flag className="h-5 w-5 text-red-500" />
                  Report Comment
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">Reason</label>
                  <select
                    value={reportCategory}
                    onChange={(e) => setReportCategory(e.target.value)}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                    data-testid="report-category-select"
                  >
                    <option value="phi">Contains patient identifiers (PHI)</option>
                    <option value="spam">Spam</option>
                    <option value="harassment">Harassment</option>
                    <option value="misinformation">Misinformation</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">Details (optional)</label>
                  <Textarea
                    value={reportReason}
                    onChange={(e) => setReportReason(e.target.value)}
                    placeholder="Describe the issue..."
                    className="min-h-[60px]"
                    data-testid="report-reason-input"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" size="sm" onClick={() => { setReportingCommentId(null); setReportReason(''); }}>
                    Cancel
                  </Button>
                  <Button size="sm" variant="destructive" onClick={handleReport} disabled={!reportReason.trim()} data-testid="submit-report-btn">
                    Submit Report
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default ThreadDetailPage;
